// Path: frontend/src/lib/apis_chatmsg.ts
import { api } from './api/core';

export interface SlideMessage {
  slide_id: number;
  slide_index: number;
  message_id: number;
  content: string;
  created_at: string;
  plan_id?: number;
  version_number?: string;
}

export interface PlanMessages {
  plan_id: number;
  version_number: string;
  slide_messages: SlideMessage[];
}

export interface LessonChatRecord {
  lesson_id: number;
  lesson_title: string;
  course_id: number;
  course_title: string;
  stage_id: number;
  stage_title: string;
  lesson_sort_order: number;
  plans: PlanMessages[];
}

export interface UserChatRecord {
  user_id: number;
  username: string;
  latest_message_time: string;
  lessons: LessonChatRecord[];
}

export interface UserChatRecordsParams {
  start_date?: string;
  end_date?: string;
  user_id?: number;
  course_id?: number;
  stage_id?: number;
  lesson_id?: number;
}

export const getUserChatRecords = async (params?: UserChatRecordsParams): Promise<UserChatRecord[]> => {
  const response = await api.get<UserChatRecord[]>('/chat-records/user-chat-records', { params });
  return response.data;
};

export interface PlanMessage {
  message_id: number;
  content: string;
  created_at: string;
}

export interface PlanDevRecord {
  plan_id: number;
  version_number: string;
  messages: PlanMessage[];
}

export interface LessonPlanChatRecord {
  lesson_id: number;
  lesson_title: string;
  course_id: number;
  course_title: string;
  stage_id: number;
  stage_title: string;
  lesson_sort_order: number;
  plans: PlanDevRecord[];
}

export interface UserPlanChatRecord {
  user_id: number;
  username: string;
  latest_message_time: string;
  lessons: LessonPlanChatRecord[];
}

export interface UserPlanChatRecordsParams {
  start_date?: string;
  end_date?: string;
}

export const getUserPlanChatRecords = async (params?: UserPlanChatRecordsParams): Promise<UserPlanChatRecord[]> => {
  const response = await api.get<UserPlanChatRecord[]>('/chat-records/user-plan-chat-records', { params });
  return response.data;
};

export interface VibeMessage {
  message_id: number;
  content: string;
  created_at: string;
}

export interface VibeFile {
  file_id: number;
  file_name: string;
  messages: VibeMessage[];
}

export interface VibeProject {
  project_id: number;
  project_name: string;
  files: VibeFile[];
}

export interface UserVibeChatRecord {
  user_id: number;
  username: string;
  user_role: string;
  user_memo: string | null;
  latest_message_time: string;
  projects: VibeProject[];
}

export interface UserVibeChatRecordsParams {
  start_date?: string;
  end_date?: string;
}

export const getUserVibeChatRecords = async (params?: UserVibeChatRecordsParams): Promise<UserVibeChatRecord[]> => {
  const response = await api.get<UserVibeChatRecord[]>('/chat-records/vibe-chat-records', { params });
  return response.data;
};

export interface SalesMessage {
  message_id: number;
  content: string;
  created_at: string;
}

export interface UserSalesChatRecord {
  user_id: number;
  username: string;
  user_role: string;
  user_memo: string | null;
  latest_message_time: string;
  messages: SalesMessage[];
}

export interface UserSalesChatRecordsParams {
  start_date?: string;
  end_date?: string;
}

export const getUserSalesChatRecords = async (params?: UserSalesChatRecordsParams): Promise<UserSalesChatRecord[]> => {
  const response = await api.get<UserSalesChatRecord[]>('/chat-records/sales-chat-records', { params });
  return response.data;
};

export interface PreviousUserMessage {
  id: number;
  user_id: number;
  username: string;
  role: 'user';
  content: string;
  input_tokens: number;
  output_tokens: number;
  created_at: string;
}

export interface RiskChatRecord {
  id: number;
  user_id: number;
  username: string;
  role: 'assistant' | 'user';
  content: string;
  input_tokens: number;
  output_tokens: number;
  tokens_total: number;
  created_at: string;
  lesson_id: number | null;
  artifact_id: number | null;
  previous_user_message: PreviousUserMessage | null;
}

export interface RiskChatRecordsResponse {
  total: number;
  items: RiskChatRecord[];
}

export interface RiskChatRecordsParams {
  start_time?: string;
  end_time?: string;
  min_tokens?: number;
  max_tokens?: number;
  limit?: number;
}

export const getSlideDevRiskRecords = async (params: RiskChatRecordsParams): Promise<RiskChatRecordsResponse> => {
  const response = await api.get<RiskChatRecordsResponse>('/chat-records/chat-slide-dev-with-previous', { params });
  return response.data;
};

export const getPlanDevRiskRecords = async (params: RiskChatRecordsParams): Promise<RiskChatRecordsResponse> => {
  const response = await api.get<RiskChatRecordsResponse>('/chat-records/chat-plan-dev-with-previous', { params });
  return response.data;
};

export interface SlideTokenChatDetail {
  id: number;
  user_id: number;
  username: string;
  role: 'user' | 'assistant';
  content: string;
  input_tokens: number;
  output_tokens: number;
  created_at: string;
}

export interface SlideTokenStatsItem {
  slide_id: number;
  slide_index: number;
  plan_id: number;
  plan_version_number: string;
  plan_created_by: number;
  username: string;
  lesson_id: number;
  lesson_title: string;
  lesson_sort_order: number;
  stage_id: number;
  stage_title: string;
  course_id: number;
  course_title: string;
  total_tokens_input: number;
  total_tokens_output: number;
  total_tokens: number;
  cost_rmb: number;
  chat_details: SlideTokenChatDetail[];
}

export interface SlideTokenStatsResponse {
  total: number;
  items: SlideTokenStatsItem[];
}

export interface SlideTokenStatsParams {
  limit?: number;
}

export const getSlideTokenStats = async (params?: SlideTokenStatsParams): Promise<SlideTokenStatsResponse> => {
  const response = await api.get<SlideTokenStatsResponse>('/chat-records/slide-token-stats/', { params });
  return response.data;
};
